import { useMemo, useState } from 'react';
import type { AdminAcquisitionFunnelRow, AdminAcquisitionGroupBy } from '@kopechka/contracts';
import { getAcquisitionFunnel } from '../api/acquisition.js';
import { useApiQuery } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  LoadingState,
  PageHead,
  Select,
  StatCard,
  TBody,
  TD,
  TH,
  THead,
  TRow,
  Table,
} from '../ui/index.js';

/**
 * D-265 — Kazanım (marketing funnel per campaign link).
 *
 * Reads GET /admin/acquisition/funnel: per source / campaign / referrer,
 * visits -> signups -> verified -> paying -> money. The link builder at the
 * bottom makes the `utm_*` link to paste into a forum post or a Telegram
 * message, so every channel shows up as its own row here.
 */

export const STOREFRONT_ORIGIN = 'https://kopechka.com';
const DAY_MS = 24 * 60 * 60 * 1000;

const GROUP_LABELS: Record<AdminAcquisitionGroupBy, string> = {
  source: 'Kaynak (utm_source / ref)',
  medium: 'Kanal türü (utm_medium)',
  campaign: 'Kampanya (utm_campaign)',
  content: 'İçerik (utm_content)',
  referrer_host: 'Yönlendiren site',
  landing_path: 'Açılış sayfası',
};

export function utcDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Default range: the last 30 UTC days including today. */
export function defaultRange(now: Date = new Date()): { from: string; to: string } {
  return { from: utcDay(new Date(now.getTime() - 29 * DAY_MS)), to: utcDay(now) };
}

/** Micro USD (1e-6) decimal string -> "12.34" (two decimals; four when below one dollar). */
export function formatMicroUsd(micro: string): string {
  const n = Number(micro);
  if (!Number.isFinite(n)) return '0.00';
  const usd = n / 1_000_000;
  return usd !== 0 && Math.abs(usd) < 1 ? usd.toFixed(4) : usd.toFixed(2);
}

export function percent(part: number, whole: number): string {
  if (whole <= 0) return '—';
  return `${((part / whole) * 100).toFixed(1)}%`;
}

export interface CampaignLinkInput {
  readonly source: string;
  readonly medium?: string;
  readonly campaign?: string;
  readonly content?: string;
  readonly path?: string;
}

/** The storefront link that carries the campaign fields. Empty source = no link. */
export function buildCampaignUrl(input: CampaignLinkInput): string | null {
  const source = input.source.trim();
  if (!source) return null;
  const params = new URLSearchParams();
  params.set('utm_source', source);
  const medium = input.medium?.trim();
  if (medium) params.set('utm_medium', medium);
  const campaign = input.campaign?.trim();
  if (campaign) params.set('utm_campaign', campaign);
  const content = input.content?.trim();
  if (content) params.set('utm_content', content);
  const path = (input.path ?? '/').trim();
  const safePath = path.startsWith('/') ? path : `/${path}`;
  return `${STOREFRONT_ORIGIN}${safePath}?${params.toString()}`;
}

function keyLabel(key: string | null): string {
  return key === null ? '(kaynaksız / doğrudan)' : key;
}

function FunnelTable({ rows }: { readonly rows: readonly AdminAcquisitionFunnelRow[] }): JSX.Element {
  return (
    <Table>
      <THead>
        <TRow>
          <TH>Değer</TH>
          <TH>Ziyaret</TH>
          <TH>Tekil</TH>
          <TH>Kayıt</TH>
          <TH>Kayıt / ziyaret</TH>
          <TH>Doğrulanan</TH>
          <TH>Yükleme yapan</TH>
          <TH>Yükleyen / kayıt</TH>
          <TH>Tekrar yükleyen</TH>
          <TH>Yükleme $</TH>
          <TH>Harcama $</TH>
          <TH>İade $</TH>
        </TRow>
      </THead>
      <TBody>
        {rows.map((row) => (
          <TRow key={row.key ?? '__none__'}>
            <TD>{keyLabel(row.key)}</TD>
            <TD>{row.visits}</TD>
            <TD>{row.visitors}</TD>
            <TD>{row.signups}</TD>
            <TD>{percent(row.signups, row.visits)}</TD>
            <TD>{row.verified}</TD>
            <TD>{row.payers}</TD>
            <TD>{percent(row.payers, row.signups)}</TD>
            <TD>{row.repeatPayers}</TD>
            <TD>{formatMicroUsd(row.topupMicro)}</TD>
            <TD>{formatMicroUsd(row.chargesMicro)}</TD>
            <TD>{formatMicroUsd(row.refundsMicro)}</TD>
          </TRow>
        ))}
      </TBody>
    </Table>
  );
}

function LinkBuilder(): JSX.Element {
  const [source, setSource] = useState('');
  const [medium, setMedium] = useState('forum');
  const [campaign, setCampaign] = useState('');
  const [content, setContent] = useState('');
  const [path, setPath] = useState('/');
  const [copied, setCopied] = useState(false);
  const url = useMemo(() => buildCampaignUrl({ source, medium, campaign, content, path }), [source, medium, campaign, content, path]);

  const copy = async (): Promise<void> => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Kampanya linki oluştur" subtitle="Forum konusuna, Telegram mesajına veya banner'a bu linki koy; tıklayan herkes yukarıdaki tabloda kendi satırında görünür." />
      <CardBody>
        <div className="form-grid" style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <Field label="Kaynak (zorunlu)" help="forum / kanal / araç adı: zennoclub, bas, r10, gsa, telegram-kanal" htmlFor="acq-source">
            <Input id="acq-source" value={source} onChange={(e) => setSource(e.target.value)} placeholder="zennoclub" />
          </Field>
          <Field label="Kanal türü" help="forum, telegram, banner, referral, dm" htmlFor="acq-medium">
            <Input id="acq-medium" value={medium} onChange={(e) => setMedium(e.target.value)} />
          </Field>
          <Field label="Kampanya" help="örn. 2026-11-lansman" htmlFor="acq-campaign">
            <Input id="acq-campaign" value={campaign} onChange={(e) => setCampaign(e.target.value)} />
          </Field>
          <Field label="İçerik" help="hangi konu / hangi banner" htmlFor="acq-content">
            <Input id="acq-content" value={content} onChange={(e) => setContent(e.target.value)} />
          </Field>
          <Field label="Açılış sayfası" help="/ veya /en/pricing/" htmlFor="acq-path">
            <Input id="acq-path" value={path} onChange={(e) => setPath(e.target.value)} />
          </Field>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <code data-testid="acq-campaign-url" style={{ wordBreak: 'break-all' }}>
            {url ?? 'Önce bir kaynak yaz.'}
          </code>
          <Button type="button" size="sm" variant="secondary" disabled={!url} onClick={() => void copy()} icon={<Icon.Copy />}>
            {copied ? 'Kopyalandı' : 'Kopyala'}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export function AcquisitionPage(): JSX.Element {
  const can = useCan('financial:review');
  if (!can) {
    return (
      <>
        <PageHead title="Kazanım" subtitle="Kampanya linki başına ziyaret, kayıt, yükleme ve para." />
        <EmptyState label="Bu sayfa için financial:review yetkisi gerekir." />
      </>
    );
  }
  return <AcquisitionFunnel />;
}

/** The funnel itself; mounted only with the capability, so no read is attempted without it. */
function AcquisitionFunnel(): JSX.Element {
  const initial = useMemo(() => defaultRange(), []);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [groupBy, setGroupBy] = useState<AdminAcquisitionGroupBy>('source');
  const query = useApiQuery(
    (options) => getAcquisitionFunnel(options, { from, to, groupBy }),
    [from, to, groupBy],
  );

  return (
    <>
      <PageHead
        title="Kazanım"
        subtitle="Hangi forum, kanal veya link müşteri getiriyor: ziyaret → kayıt → doğrulama → ilk yükleme → para. Günler UTC; para dolar."
      />
      <Card>
        <CardBody>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Field label="Başlangıç" htmlFor="acq-from">
              <Input id="acq-from" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label="Bitiş" htmlFor="acq-to">
              <Input id="acq-to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
            </Field>
            <Field label="Gruplama" htmlFor="acq-group">
              <Select id="acq-group" value={groupBy} onChange={(e) => setGroupBy(e.target.value as AdminAcquisitionGroupBy)}>
                {(Object.keys(GROUP_LABELS) as AdminAcquisitionGroupBy[]).map((key) => (
                  <option key={key} value={key}>
                    {GROUP_LABELS[key]}
                  </option>
                ))}
              </Select>
            </Field>
            <Button type="button" variant="secondary" onClick={query.reload} loading={query.refreshing} icon={<Icon.Refresh />}>
              Yenile
            </Button>
          </div>
        </CardBody>
      </Card>

      {query.kind === 'loading' || query.kind === 'idle' ? <LoadingState label="Huni yükleniyor…" /> : null}
      {query.kind === 'auth_required' ? <ErrorState reason="Oturum gerekli." /> : null}
      {query.kind === 'error' ? <ErrorState reason={query.reason} onRetry={query.reload} /> : null}
      {query.kind === 'ok' ? (
        <>
          <div className="stat-grid" style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', margin: '12px 0' }}>
            <StatCard label="Ziyaret" value={query.data.totals.visits} meta={`${query.data.totals.visitors} tekil`} testId="acq-total-visits" />
            <StatCard label="Kayıt" value={query.data.totals.signups} meta={`${percent(query.data.totals.signups, query.data.totals.visits)} / ziyaret`} testId="acq-total-signups" />
            <StatCard label="Doğrulanan" value={query.data.totals.verified} />
            <StatCard label="Yükleme yapan" value={query.data.totals.payers} meta={`${percent(query.data.totals.payers, query.data.totals.signups)} / kayıt`} testId="acq-total-payers" />
            <StatCard label="Yükleme $" value={formatMicroUsd(query.data.totals.topupMicro)} testId="acq-total-topup" />
            <StatCard label="Harcama $" value={formatMicroUsd(query.data.totals.chargesMicro)} meta={`iade ${formatMicroUsd(query.data.totals.refundsMicro)}`} />
          </div>
          <Card>
            <CardHeader title={GROUP_LABELS[groupBy]} subtitle={`${query.data.from} – ${query.data.to} · en fazla 200 satır, kayıt sayısına göre`} />
            <CardBody>
              {query.data.rows.length === 0 ? (
                <EmptyState label="Bu aralıkta ziyaret veya kayıt yok." hint="Bir kampanya linki oluşturup paylaş; ilk tıklama bu tabloya düşer." />
              ) : (
                <FunnelTable rows={query.data.rows} />
              )}
            </CardBody>
          </Card>
        </>
      ) : null}

      <div style={{ marginTop: 16 }}>
        <LinkBuilder />
      </div>
    </>
  );
}
